import React from 'react'
import { faGreaterThan, faLessThan } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import styled, { css } from 'styled-components'
import DcFlexBox from '../dc-flex-box/dc-flex-box'
import { $DCprimaryColor, colors } from '../styleGuide'

export interface DcTabsProps {
  className?: string
  tabs: { value: any; label: string; icon?: React.ReactNode; key?: string }[]
  activeTab: string | null
  handleChange: (value: any) => void
  variant?:
    | 'default'
    | 'pills'
    | 'pills-column'
    | 'outline-pills'
    | 'simple'
    | 'sub-simple'
  size?: 'sm' | 'md' | 'lg' | 'small'
  withSubTabs?: boolean
  subTabs?: { value: any; label: string; key?: string }[]
}

export const DcTabs = React.forwardRef<HTMLDivElement, DcTabsProps>(
  (
    {
      className,
      tabs,
      activeTab,
      handleChange,
      variant,
      size,
      withSubTabs,
      subTabs,
      ...rest
    },
    ref,
  ) => {
    const scrollable = React.useRef<HTMLUListElement>(null)
    const reference = React.useRef<HTMLLIElement>(null)
    const [scrollX, setScrollX] = React.useState(0)
    const [scrollEnd, setScrollEnd] = React.useState(true)

    const slide = (shift: number) => {
      if (scrollable.current) {
        scrollable.current.scrollLeft += shift
        setScrollX(scrollable.current.scrollLeft)
        setScrollEnd(
          Math.floor(
            scrollable.current.scrollWidth - scrollable.current.scrollLeft,
          ) <= scrollable.current.offsetWidth,
        )
      }
    }

    const scrollCheck = () => {
      if (scrollable.current) {
        setScrollX(scrollable.current.scrollLeft)
        setScrollEnd(
          Math.floor(
            scrollable.current.scrollWidth - scrollable.current.scrollLeft,
          ) <= scrollable.current.offsetWidth,
        )
      }
    }

    React.useEffect(() => {
      scrollCheck()
    }, [tabs])

    return (
      <TabsContainer
        variant={variant}
        size={size}
        className={className}
        ref={ref}
        {...rest}
      >
        {scrollX !== 0 && (
          <Arrow onClick={() => slide(-50)}>
            <FontAwesomeIcon icon={faLessThan} />
          </Arrow>
        )}
        <Tabs
          variant={variant}
          ref={scrollable}
          withSubTabs={withSubTabs}
          onScroll={scrollCheck}
        >
          {tabs.map((item) => (
            <Tab
              ref={reference}
              style={
                withSubTabs && activeTab === item.value
                  ? { position: 'relative' }
                  : {}
              }
              variant={variant}
              key={item.value}
              className={activeTab === item.value ? 'active' : ''}
              onClick={() => handleChange(item.value)}
            >
              <TabContent
                size={size}
                variant={variant}
                className={activeTab === item.value ? 'active' : ''}
              >
                {item.icon && <TabIcon>{item.icon}</TabIcon>}
                {item.label}
              </TabContent>
              {withSubTabs && activeTab === item.value && (
                <DcFlexBox
                  style={{
                    position: 'absolute',
                    top: '50px',
                    width: 'auto',
                    zIndex: 1,
                    background: colors.white,
                    border: `1px solid ${colors.grayBorderLight}`,
                    padding: '10px',
                    display: 'flex',
                    borderRadius: '5px',
                  }}
                >
                  {subTabs?.map((subItem) => (
                    <TabContent
                      size={size}
                      variant={variant}
                      key={subItem.value}
                      className={activeTab === 'subtabs' ? 'active' : ''}
                      onClick={() => handleChange('subtabs')}
                    >
                      {subItem.label}
                    </TabContent>
                  ))}
                </DcFlexBox>
              )}
            </Tab>
          ))}
        </Tabs>
        {!scrollEnd && (
          <Arrow onClick={() => slide(50)}>
            <FontAwesomeIcon icon={faGreaterThan} />
          </Arrow>
        )}
      </TabsContainer>
    )
  },
)

type TabVariant =
  | 'default'
  | 'pills'
  | 'pills-column'
  | 'outline-pills'
  | 'simple'
  | 'sub-simple'

interface TabProps {
  variant?: TabVariant
  position?: string
}

interface ThemeProps {
  size?: 'sm' | 'md' | 'lg' | 'small'
}

const TabsContainer = styled.div<{
  variant?: TabVariant
  size?: 'sm' | 'md' | 'lg' | 'small'
}>`
  display: flex;
  align-items: center;
  overflow: visible;
`

const Tabs = styled.ul<{
  variant?: TabVariant
  withSubTabs?: boolean
}>`
  display: flex;
  list-style: none;
  overflow-x: ${(props) => (props.withSubTabs ? 'visible' : 'scroll')};
  scroll-behavior: smooth;
  margin: 0;
  padding: 0;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }

  ${(props) =>
    props.variant === 'default' &&
    css`
      border-radius: 5px;
      overflow: visible;
      border-bottom: 2px solid ${colors.grayBorderLight} !important;
    `}

  ${(props) =>
    props.variant === 'outline-pills' &&
    css`
      border-bottom: none;
    `}

  ${(props) =>
    props.variant === 'pills' &&
    css`
      border-bottom: none;
    `}

  ${(props) =>
    props.variant === 'simple' &&
    css`
      border-bottom: none;
    `}
`

const Tab = styled.li<TabProps>`
  flex: 0 0 auto;
  cursor: pointer;
  position: ${(props) => props.position};

  ${(props) =>
    props.variant === 'default' &&
    css`
      padding: 0px !important;
    `}

  ${(props) =>
    props.variant === 'simple' &&
    css`
      padding: 0px !important;
      border-radius: none;
    `}

  ${(props) =>
    props.variant === 'pills' &&
    css`
      padding: 0px !important;
    `}

  ${(props) =>
    props.variant === 'outline-pills' &&
    css`
      padding: 0px !important;
    `}
`

interface TabContentProps {
  variant?: TabVariant
  size?: 'sm' | 'md' | 'lg' | 'small'
  position?: string
}

const TabContent = styled.div<TabContentProps>`
  position: ${(props) => props.position};
  font-size: ${(props: ThemeProps) =>
    props.size === 'sm'
      ? '0.75rem'
      : props.size === 'md'
      ? '0.875rem'
      : '1rem'};
  ${(props: ThemeProps) =>
    props.size === 'small' &&
    css`
      font-size: 10px !important;
    `}
  display: flex;
  align-items: center;
  padding: ${(props: ThemeProps) =>
    props.size === 'sm'
      ? '2px 8px'
      : props.size === 'md'
      ? '5px 10px'
      : '9px 18px'};
  ${(props: ThemeProps) =>
    props.size === 'small' &&
    css`
      padding: 4px 10px;
    `}
  color: ${colors.grayTextMedium};
  transition: all 0.2s ease-in-out;

  &.active {
    color: ${$DCprimaryColor};
    position: relative;
    ${(props) =>
      props.variant === 'default' &&
      css`
        &::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 100%;
          height: 2px;
          border-radius: 2px;
        }
      `}
  }

  ${(props) =>
    props.variant === 'default' &&
    css`
      padding: ${(props: ThemeProps) =>
        props.size === 'sm'
          ? '2px 8px'
          : props.size === 'md'
          ? '5px 10px'
          : '9px 18px'};
      border-top: 1px solid transparent;
      border-right: 1px solid transparent;
      border-left: 1px solid transparent;
      border-bottom: 1px solid ${colors.grayBorderLight};

      &:hover {
        color: ${$DCprimaryColor};
        border-radius: 0.375rem 0.375rem 0 0 !important;
        padding: ${(props: ThemeProps) =>
          props.size === 'sm'
            ? '2px 8px'
            : props.size === 'md'
            ? '5px 10px'
            : '9px 18px'};
        border-top: 1px solid ${colors.grayBorderMedium} !important;
        border-right: 1px solid ${colors.grayBorderMedium} !important;
        border-left: 1px solid ${colors.grayBorderMedium} !important;
      }
      &.active {
        color: ${$DCprimaryColor};
        border-radius: 0.375rem 0.375rem 0 0 !important;
        padding: ${(props: ThemeProps) =>
          props.size === 'sm'
            ? '2px 8px'
            : props.size === 'md'
            ? '5px 10px'
            : '9px 18px'};
        border-top: 1px solid ${colors.grayBorderMedium} !important;
        border-right: 1px solid ${colors.grayBorderMedium} !important;
        border-left: 1px solid ${colors.grayBorderMedium} !important;
        border-bottom: none;
      }
    `}

  ${(props) =>
    props.variant === 'pills' &&
    css`
      padding: ${(props: ThemeProps) =>
        props.size === 'sm'
          ? '2px 8px'
          : props.size === 'md'
          ? '5px 10px'
          : '9px 18px'};
      border: 1px solid ${colors.grayBorderLight};
      border-radius: 0.375rem;
      margin-right: 5px;
      margin-bottom: 5px;
      &:hover {
        color: ${$DCprimaryColor};
      }
      &.active {
        color: ${colors.white};
        background-color: ${$DCprimaryColor};
        border-radius: 0.375rem !important;
      }
    `}

  ${(props) =>
    props.variant === 'outline-pills' &&
    css`
      padding: ${(props: ThemeProps) =>
        props.size === 'sm'
          ? '2px 8px'
          : props.size === 'md'
          ? '5px 10px'
          : '9px 18px'};
      border: 1px solid transparent;
      &:hover {
        color: ${$DCprimaryColor};
      }
      &.active {
        color: ${$DCprimaryColor};
        background-color: ${colors.white};
        border-radius: 0.375rem !important;
        border: 1px solid ${$DCprimaryColor};
      }
    `}

  ${(props) =>
    props.variant === 'simple' &&
    css`
      padding: ${(props: ThemeProps) =>
        props.size === 'sm'
          ? '2px 8px'
          : props.size === 'md'
          ? '5px 10px'
          : props.size === 'small'
          ? '4px 10px'
          : '9px 18px'};
      border: 1px solid transparent;

      &:hover {
        color: ${$DCprimaryColor};
      }
      &.active {
        color: ${$DCprimaryColor};
        background-color: ${colors.white};
        font-weight: 600;
      }
    `}

  ${(props) =>
    props.variant === 'sub-simple' &&
    css`
      border: 1px solid ${colors.grayBorderLight};
      margin-right: 5px;
      border-radius: 4px;
      padding: ${(props: ThemeProps) =>
        props.size === 'sm'
          ? '2px 8px'
          : props.size === 'md'
          ? '5px 10px'
          : props.size === 'small'
          ? '4px 10px'
          : '9px 18px'};

      &:hover {
        color: ${$DCprimaryColor};
      }
      &.active {
        color: ${$DCprimaryColor};
        background-color: ${colors.white};
        font-weight: 600;
      }
    `}
`

const Arrow = styled.button`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${colors.grayTextMedium};
  transition: color 0.3s ease-in-out;

  &:hover {
    color: ${$DCprimaryColor};
  }
`

const TabIcon = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default DcTabs
